"use client";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { User, X, Phone, Cake } from "lucide-react";
import {
  FaCreditCard,
  FaTriangleExclamation,
  FaCheck,
  FaPlus,
  FaArrowsRotate,
} from "react-icons/fa6";
import { MOCK_APPOINTMENTS, MOCK_PATIENTS, Patient } from "@/components/data";
import cn from "@/lib/cn";
import { nowTimeStr, parseTime } from "@/lib/time";
import { PatientDetailsDrawer } from "@/features/patient";
import { CcofCardOnFile } from "lib/api-types/schemas";

/* ─── Filled icon components ─────────────────────────────────── */
const FilledChat = ({
  size = 14,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill={color}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M16 3C8.28 3 2 8.04 2 14.2c0 3.56 1.94 6.74 5 8.88-.12 1.52-.68 3.24-1.92 4.92 3.04-.12 5.84-1.36 7.56-2.72 1.08.2 2.2.32 3.36.32 7.72 0 14-5.04 14-11.2S23.72 3 16 3Z" />
  </svg>
);

/* ─── Class helpers ───────────────────────────────────────── */

function progressWidthClass(progressPercent: number) {
  if (progressPercent >= 99.5) return "w-full";
  if (progressPercent >= 94) return "w-[94.117647%]";
  if (progressPercent >= 88) return "w-[88.235294%]";
  return "w-0";
}

/* ─── Time helpers ──────────────────────────────────────────── */

function findPatient(patientId: string): Patient | undefined {
  return MOCK_PATIENTS.find((p) => p.id === patientId);
}

interface LinkStatus {
  sentAt: string;
  sendCount: number;
}

/* ═══════════════════════════════════════════════════════════════
 *  TodayViewV5 — Flat list, no schedule grouping
 * ═══════════════════════════════════════════════════════════════ */

export default function TodayViewV5({
  ccofUpdateSignal = 0,
}: {
  ccofUpdateSignal?: number;
}) {
  const [activeFilter, setActiveFilter] = useState<"all" | "noccof">("all");
  const [providerToggles, setProviderToggles] = useState<
    Record<string, boolean>
  >(() => {
    const init: Record<string, boolean> = {};
    for (const a of MOCK_APPOINTMENTS) init[a.provider] = true;
    return init;
  });
  const [hovRow, setHovRow] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiRef = useRef<HTMLCanvasElement | null>(null);

  // Tray state
  const [trayPatientId, setTrayPatientId] = useState<string | null>(null);

  // CCOF state — first appointment's patient has a card on file
  const [ccofCards, setCcofCards] = useState<Record<string, CcofCardOnFile>>(
    () => {
      // All patients except David Park (P009) have a card on file
      const cards: Record<string, CcofCardOnFile> = {
        P006: {
          brand: "MC",
          last4: "8291",
          expMonth: 11,
          expYear: 2027,
          cardHolderName: "Jennifer Walsh",
        },
        P001: {
          brand: "VISA",
          last4: "4521",
          expMonth: 3,
          expYear: 2028,
          cardHolderName: "Michael Thompson",
        },
        P002: {
          brand: "MC",
          last4: "7734",
          expMonth: 9,
          expYear: 2027,
          cardHolderName: "Alice Thompson",
        },
        P007: {
          brand: "VISA",
          last4: "3318",
          expMonth: 6,
          expYear: 2028,
          cardHolderName: "Robert Chen",
        },
        P008: {
          brand: "MC",
          last4: "5590",
          expMonth: 1,
          expYear: 2029,
          cardHolderName: "Maria Gonzalez",
        },
        P003: {
          brand: "VISA",
          last4: "2201",
          expMonth: 8,
          expYear: 2027,
          cardHolderName: "Sarah Thompson",
        },
        P010: {
          brand: "MC",
          last4: "6643",
          expMonth: 12,
          expYear: 2028,
          cardHolderName: "James Wilson",
        },
        P011: {
          brand: "VISA",
          last4: "9912",
          expMonth: 5,
          expYear: 2028,
          cardHolderName: "Lisa Anderson",
        },
        P012: {
          brand: "MC",
          last4: "1187",
          expMonth: 4,
          expYear: 2027,
          cardHolderName: "Kevin Brown",
        },
        P013: {
          brand: "VISA",
          last4: "8805",
          expMonth: 10,
          expYear: 2028,
          cardHolderName: "Amanda Torres",
        },
        P004: {
          brand: "MC",
          last4: "3347",
          expMonth: 7,
          expYear: 2027,
          cardHolderName: "Michael Johnson",
        },
        P014: {
          brand: "VISA",
          last4: "6629",
          expMonth: 2,
          expYear: 2029,
          cardHolderName: "Emma Davis",
        },
        P005: {
          brand: "MC",
          last4: "4410",
          expMonth: 11,
          expYear: 2028,
          cardHolderName: "Michael Martinez",
        },
        P015: {
          brand: "VISA",
          last4: "7756",
          expMonth: 9,
          expYear: 2028,
          cardHolderName: "Nicole White",
        },
        P016: {
          brand: "MC",
          last4: "2298",
          expMonth: 6,
          expYear: 2027,
          cardHolderName: "Rachel Patel",
        },
        P017: {
          brand: "VISA",
          last4: "5534",
          expMonth: 3,
          expYear: 2029,
          cardHolderName: "Daniel Kim",
        },
      };
      return cards;
    },
  );
  const [linkStatuses, setLinkStatuses] = useState<Record<string, LinkStatus>>(
    {},
  );
  const [recentSends, setRecentSends] = useState<Record<string, boolean>>({});

  // Simulate Michael Thompson (P001) filling out CCOF remotely when signal fires
  const ccofSignalRef = useRef(0);
  useEffect(() => {
    if (ccofUpdateSignal > 0 && ccofUpdateSignal !== ccofSignalRef.current) {
      ccofSignalRef.current = ccofUpdateSignal;
      setCcofCards((prev) => ({
        ...prev,
        P001: {
          brand: "VISA",
          last4: "9876",
          expMonth: 12,
          expYear: 2032,
          cardHolderName: "Michael Thompson",
        },
      }));
    }
  }, [ccofUpdateSignal]);

  const appointments = MOCK_APPOINTMENTS;

  /* ── Derived ────────────────────────────────────────────── */

  const hasCCOF = useCallback(
    (patientId: string) => !!ccofCards[patientId],
    [ccofCards],
  );

  const filteredAppts = useMemo(() => {
    let list = appointments.filter(
      (a) => providerToggles[a.provider] !== false,
    );
    if (activeFilter === "noccof")
      list = list.filter((a) => !hasCCOF(a.patientId));
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments, activeFilter, providerToggles, ccofCards, refreshKey]);

  const noCCOFCount = useMemo(
    () =>
      appointments.filter(
        (a) => providerToggles[a.provider] !== false && !hasCCOF(a.patientId),
      ).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointments, providerToggles, ccofCards, refreshKey],
  );

  const totalCount = appointments.filter(
    (a) => providerToggles[a.provider] !== false,
  ).length;
  const collectedCount = totalCount - noCCOFCount;
  const progressPercent =
    totalCount > 0 ? (collectedCount / totalCount) * 100 : 0;
  const allCollected = collectedCount === totalCount;

  /* ── Confetti effect when 100% collected ──────────────────── */
  const prevAllCollectedRef = useRef(false);
  useEffect(() => {
    if (allCollected && !prevAllCollectedRef.current && totalCount > 0) {
      setShowConfetti(true);
      const canvas = confettiRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const colors = [
        "#1884F9",
        "#00A67F",
        "#F59E0B",
        "#EF4444",
        "#8B5CF6",
        "#EC4899",
        "#14B8A6",
        "#F97316",
      ];
      const particles: {
        x: number;
        y: number;
        w: number;
        h: number;
        color: string;
        vx: number;
        vy: number;
        rot: number;
        rotV: number;
        opacity: number;
        shape: number;
      }[] = [];

      for (let i = 0; i < 200; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: -20 - Math.random() * canvas.height * 0.6,
          w: 6 + Math.random() * 6,
          h: 4 + Math.random() * 4,
          color: colors[Math.floor(Math.random() * colors.length)],
          vx: (Math.random() - 0.5) * 3,
          vy: 2 + Math.random() * 4,
          rot: Math.random() * Math.PI * 2,
          rotV: (Math.random() - 0.5) * 0.15,
          opacity: 1,
          shape: Math.floor(Math.random() * 3), // 0=rect, 1=circle, 2=strip
        });
      }

      let frame: number;
      let fadeStart = 0;
      const duration = 4000;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (elapsed > duration * 0.6 && fadeStart === 0) fadeStart = elapsed;

        for (const p of particles) {
          p.x += p.vx;
          p.vy += 0.08; // gravity
          p.y += p.vy;
          p.vx *= 0.99;
          p.rot += p.rotV;

          if (fadeStart > 0) {
            p.opacity = Math.max(
              0,
              1 - (elapsed - fadeStart) / (duration * 0.4),
            );
          }

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.globalAlpha = p.opacity;
          ctx.fillStyle = p.color;

          if (p.shape === 0) {
            ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          } else if (p.shape === 1) {
            ctx.beginPath();
            ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-1, -p.h, 2, p.h * 2);
          }
          ctx.restore();
        }

        if (elapsed < duration) {
          frame = requestAnimationFrame(animate);
        } else {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          setShowConfetti(false);
        }
      };

      frame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(frame);
    }
    prevAllCollectedRef.current = allCollected;
  }, [allCollected, totalCount]);

  /* ── Flat sorted list by time ────────────────────────────── */

  const sortedAppts = useMemo(() => {
    return [...filteredAppts].sort(
      (a, b) => parseTime(a.time) - parseTime(b.time),
    );
  }, [filteredAppts]);

  const dateStr = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  /* ── Handlers ───────────────────────────────────────────── */

  const handleRefresh = () => {
    setSpinning(true);
    setRefreshKey((k) => k + 1);
    setTimeout(() => setSpinning(false), 800);
  };

  const handleSendLink = (patientId: string) => {
    setLinkStatuses((prev) => {
      const existing = prev[patientId];
      return {
        ...prev,
        [patientId]: {
          sentAt: nowTimeStr(),
          sendCount: existing ? existing.sendCount + 1 : 1,
        },
      };
    });
    setRecentSends((prev) => ({ ...prev, [patientId]: true }));
    setTimeout(() => {
      setRecentSends((prev) => ({ ...prev, [patientId]: false }));
    }, 10000);
  };

  const openTray = (patientId: string) => setTrayPatientId(patientId);
  const closeTray = () => setTrayPatientId(null);

  /* helper */
  const wasSent = (patientId: string) => !!linkStatuses[patientId];

  /* ═══════════════════════════════════════════════════════════
   *  RENDER
   * ═══════════════════════════════════════════════════════════ */

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-[#FAFAFA]">
      {/* ─── Page Header ──────── */}
      <div className="shrink-0 border-b border-[#EBEDF2] bg-white px-5 pt-5">
        <div className="mb-5">
          <div className="mb-2 flex items-center justify-between">
            <h1 className="m-0 text-xl leading-none font-[var(--font-body)] font-bold tracking-[-0.25px] text-[#111827]">
              Card Collection Queue
            </h1>
          </div>
          <p className="mt-0 mb-3 text-[13px] font-[var(--font-body)] text-[#6B7280]">
            {dateStr} &middot; {appointments.length} patients scheduled
          </p>
          {/* Progress bar with blue gradient card */}
          <div className="rounded-[16px] bg-[linear-gradient(135deg,#1884F9,#619BFF)] px-5 py-4 shadow-[0_2px_8px_rgba(24,132,249,0.25)]">
            <div className="flex w-full items-center gap-[10px]">
              <span className="inline-flex shrink-0 items-center rounded-[999px] bg-[rgba(255,255,255,0.20)] px-[10px] py-1 text-[12px] font-[var(--font-body)] font-bold whitespace-nowrap text-white">
                {Math.round(progressPercent)}% collected
              </span>
              <div className="h-[10px] flex-1 overflow-hidden rounded-[999px] bg-[rgba(255,255,255,0.25)]">
                <div
                  className={cn(
                    "h-full rounded-[999px] bg-white transition-[width] duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
                    progressWidthClass(progressPercent),
                  )}
                />
              </div>
              <span className="inline-flex shrink-0 items-center rounded-[999px] bg-[rgba(255,255,255,0.20)] px-[10px] py-1 text-[12px] font-[var(--font-body)] font-bold whitespace-nowrap text-white">
                {collectedCount}/{totalCount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Main full-width layout ─────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden p-5">
        {/* Filter pills + Refresh — single row */}
        <div className="mb-3.5 flex items-center gap-1.5">
          {[
            {
              key: "all" as const,
              label: "All",
              count: appointments.filter(
                (a) => providerToggles[a.provider] !== false,
              ).length,
            },
            {
              key: "noccof" as const,
              label: "No Card on File",
              count: noCCOFCount,
            },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={cn(
                "flex cursor-pointer items-center gap-1.5 rounded-full border-0 px-3 py-1.5 text-xs font-[var(--font-body)] font-semibold whitespace-nowrap transition-all duration-150",
                activeFilter === f.key
                  ? "bg-[#111827] text-white"
                  : "bg-[#F3F4F6] text-[#6B7280]",
              )}
            >
              {f.label}
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[11px] font-bold",
                  activeFilter === f.key ? "bg-white/20" : "bg-[#E5E7EB]",
                )}
              >
                {f.count}
              </span>
            </button>
          ))}
          <div className="flex-1" />
          <button
            onClick={handleRefresh}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[10px] border border-[#EBEDF2] bg-transparent px-3 py-1.5 text-xs font-[var(--font-body)] font-semibold text-[#6B7280] transition-all duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB]"
          >
            <FaArrowsRotate
              className={cn(
                "text-sm transition-transform duration-[800ms] ease-out",
                spinning ? "rotate-[360deg]" : "rotate-0",
              )}
            />
            Refresh
          </button>
        </div>

        {/* Patient list in a single white card */}
        <div className="flex-1 overflow-y-auto rounded-[16px] border border-[#EBEDF2] bg-white">
          {/* Column headers inside the card — matches row layout exactly */}
          <div className="sticky top-0 z-[2] flex items-center rounded-t-[16px] border-b-[2px] border-[#EBEDF2] bg-[#F9FAFB] px-[12px]">
            {/* Matches accent strip width */}
            <div className="w-1 shrink-0" />
            {/* Matches content div padding + gap structure */}
            <div className="flex flex-1 items-center gap-[12px] px-[16px] py-[14px]">
              <span className="w-[62px] shrink-0 text-[11px] font-[var(--font-body)] font-bold tracking-[0.06em] text-[#374151] uppercase">
                Time
              </span>
              {/* No spacer — Patient header aligns with icon badges */}
              <span className="flex-1 text-[11px] font-[var(--font-body)] font-bold tracking-[0.06em] text-[#374151] uppercase">
                Patient
              </span>
              <span className="w-[200px] shrink-0 text-[11px] font-[var(--font-body)] font-bold tracking-[0.06em] text-[#374151] uppercase">
                Card on File
              </span>
              <span className="w-[260px] shrink-0 text-[11px] font-[var(--font-body)] font-bold tracking-[0.06em] text-[#374151] uppercase">
                Actions
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 p-3">
            {sortedAppts.map((appt) => {
              const isHov = hovRow === appt.id;
              const isTrayOpen = trayPatientId === appt.patientId;
              const pat = findPatient(appt.patientId);
              const hasCard = hasCCOF(appt.patientId);
              const sent = wasSent(appt.patientId);

              return (
                <div key={appt.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${appt.patientName} card collection details`}
                    onClick={() => openTray(appt.patientId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openTray(appt.patientId);
                      }
                    }}
                    onMouseEnter={() => setHovRow(appt.id)}
                    onMouseLeave={() => setHovRow(null)}
                    className={cn(
                      "flex w-full cursor-pointer items-stretch gap-0 overflow-hidden rounded-[16px] border-0 p-0 transition-all duration-150 outline-none",
                      isTrayOpen || isHov
                        ? "bg-[#EBF5FF] shadow-[0_1px_4px_rgba(24,132,249,0.15),0_1px_2px_rgba(10,10,10,0.05)]"
                        : "bg-white shadow-[0_1px_2px_rgba(10,10,10,0.05),0_1px_4px_rgba(10,10,10,0.10)]",
                    )}
                  >
                    {/* Left accent strip — red only for missing card */}
                    <div
                      className={cn(
                        "w-1 shrink-0",
                        hasCard ? "bg-[#00A67F]" : "bg-[#FF2D00]",
                      )}
                    />
                    {/* Card content */}
                    <div className="flex flex-1 items-center gap-3 px-4 py-3">
                      {/* Time label */}
                      <span className="w-[62px] shrink-0 text-xs font-[var(--font-body)] font-medium text-[#9CA3AF] tabular-nums">
                        {appt.time}
                      </span>

                      {/* Avatar */}
                      <div
                        className={cn(
                          "flex size-[34px] shrink-0 items-center justify-center rounded-[10px]",
                          hasCard ? "bg-[#00A67F]" : "bg-[#FF2D00]",
                        )}
                      >
                        {hasCard ? (
                          <FaCheck className="size-4 text-white" />
                        ) : (
                          <FaCreditCard className="size-4 text-white" />
                        )}
                      </div>

                      {/* Patient info */}
                      <div className="min-w-0 flex-1">
                        <span className="block overflow-hidden text-[13px] leading-[1.3] font-[var(--font-body)] font-semibold text-ellipsis whitespace-nowrap text-[#111827]">
                          {appt.patientName}
                        </span>
                        <span className="mt-0.5 flex items-center gap-2 text-xs font-[var(--font-body)] font-medium text-[#6B7280]">
                          {pat && (
                            <>
                              <span className="inline-flex items-center gap-[3px]">
                                <Cake
                                  className="size-3 text-[#9CA3AF]"
                                  strokeWidth={1.8}
                                />
                                {pat.dob}
                              </span>
                              <span className="inline-flex items-center gap-[3px] tabular-nums">
                                <User
                                  className="size-3 text-[#9CA3AF]"
                                  strokeWidth={1.8}
                                />
                                {pat.mrn}
                              </span>
                              <span className="inline-flex items-center gap-[3px]">
                                <Phone
                                  className="size-3 text-[#9CA3AF]"
                                  strokeWidth={1.8}
                                />
                                {pat.phone}
                              </span>
                            </>
                          )}
                        </span>
                      </div>

                      {/* Card on File column */}
                      <div className="flex w-[200px] shrink-0 items-center">
                        {hasCard ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#EAFBF3] px-2.5 py-1 text-[11px] font-[var(--font-body)] font-bold whitespace-nowrap text-[#00A67F]">
                            <FaCreditCard size={12} />
                            {ccofCards[appt.patientId].brand}{" "}
                            {ccofCards[appt.patientId].last4}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEEEE] px-2.5 py-1 text-[11px] font-[var(--font-body)] font-bold whitespace-nowrap text-[#FF2D00]">
                            <FaTriangleExclamation size={12} />
                            No Card
                          </span>
                        )}
                      </div>

                      {/* Actions column */}
                      <div className="flex w-[260px] shrink-0 items-center justify-start gap-1.5">
                        {hasCard ? null : (
                          <>
                            {sent && recentSends[appt.patientId] ? (
                              <span className="inline-flex items-center gap-2 rounded-[10px] border border-[#1884F9] bg-[#1884F9] px-4 py-2 text-[13px] font-[var(--font-body)] font-semibold whitespace-nowrap text-white">
                                <span className="flex size-3.5 shrink-0">
                                  <FaCheck className="size-3.5 text-white" />
                                </span>
                                SMS Sent
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendLink(appt.patientId);
                                }}
                                className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-[#111827] bg-[#111827] px-4 py-2 text-[13px] font-[var(--font-body)] font-semibold whitespace-nowrap text-white transition-opacity duration-150 hover:opacity-[0.88]"
                              >
                                <span className="flex size-4 shrink-0">
                                  <FilledChat size={16} color="white" />
                                </span>
                                Request via SMS
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openTray(appt.patientId);
                              }}
                              className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-[#EBEDF2] bg-[#F3F4F6] px-4 py-2 text-[13px] font-[var(--font-body)] font-semibold whitespace-nowrap text-[#6B7280] transition-all duration-150 hover:border-[#D1D5DB] hover:text-[#111827]"
                            >
                              <span className="flex size-3 shrink-0">
                                <FaPlus className="size-3 text-current" />
                              </span>
                              Enter
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
       *  CCOF TRAY
       * ═══════════════════════════════════════════════════════ */}
      <PatientDetailsDrawer
        patientId={trayPatientId}
        onClose={closeTray}
        onLinkSent={handleSendLink}
      />

      {/* Confetti canvas */}
      <canvas
        ref={confettiRef}
        className={cn(
          "pointer-events-none fixed inset-0 z-[100]",
          showConfetti ? "block" : "hidden",
        )}
      />

      {/* Champion overlay */}
      {showConfetti && (
        <div
          onClick={() => setShowConfetti(false)}
          className="animate-champion-fade-in fixed inset-0 z-[101] flex cursor-pointer items-center justify-center bg-black/35"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="animate-champion-pop-in relative flex max-w-[400px] flex-col items-center rounded-3xl bg-white px-14 py-12 shadow-[0_24px_64px_rgba(0,0,0,0.2)]"
          >
            {/* Close button */}
            <button
              onClick={() => setShowConfetti(false)}
              className="absolute top-4 right-4 flex size-8 cursor-pointer items-center justify-center rounded-full border-0 bg-[#F3F4F6] transition-colors duration-150 hover:bg-[#E5E7EB]"
            >
              <X className="size-4 text-[#6B7280]" strokeWidth={2} />
            </button>
            {/* Blue gradient circle with badge icon */}
            <div className="mb-5 flex size-20 items-center justify-center rounded-full bg-[linear-gradient(135deg,#1884F9,#619BFF)] shadow-[0_8px_24px_rgba(24,132,249,0.3)]">
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="m-0 mb-2 text-center text-[26px] leading-[1.2] font-[var(--font-body)] font-extrabold tracking-[-0.5px] text-[#111827]">
              You&apos;re a Champion!
            </h2>
            <p className="m-0 text-center text-[15px] leading-[1.5] font-[var(--font-body)] font-medium text-[#6B7280]">
              All cards on file collected
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
